import React, { useState, useRef } from 'react';
import { LuChevronDown } from "react-icons/lu";
import SplitText from "../../../common/SplitText";

const faqs = [
    {
        question: "Why partner with Rainbow Trucks rather than starting from scratch?",
        answer: "Building a food business from the ground up involves significant trial and error. Over the years, we've invested heavily in perfecting our recipes, operations, and business model. By partnering with us, you gain immediate access to a proven playbook—allowing you to bypass costly mistakes, skip the guesswork, and step directly into a scalable, high-demand business."
    },
    {
        question: "What is the initial investment required?",
        answer: "We offer flexible entry points to suit your budget. Our franchise models start at ₹3 Lakhs for the Economy Cart, ₹5 Lakhs for the Mid-Range Cart, and ₹10 Lakhs for the Premium Cart. This all-inclusive initial investment covers the cart setup, your initial product supply, and the franchise fee."
    },
    {
        question: "How does the revenue model work?",
        answer: "Your profit margins are straightforward and highly competitive. You purchase our premium kulfi at a wholesale rate of ₹30 per unit and retail it at ₹50, securing a clean ₹20 profit per sale. We only charge a nominal 5% royalty on your monthly gross sales to cover ongoing brand support and marketing."
    },
    {
        question: "What kind of ROI can I expect?",
        answer: "While returns vary by location, a standard cart selling just 2,000 kulfis per month at ₹50 each yields substantial profit margins. Thanks to low operational overhead and high product demand, most of our franchise partners see an excellent return on investment within their first few quarters."
    },
    {
        question: "Do I need prior experience in the food industry?",
        answer: "Not at all. We provide comprehensive, hands-on training that covers everything from product handling and cart operations to customer service and local marketing. If you bring the drive to succeed, we'll teach you the rest."
    },
    {
        question: "Will I have an exclusive territory?",
        answer: "Absolutely. We carefully map out our franchise locations to ensure you have a dedicated, protected territory. You will never have to worry about competing with another Rainbow Trucks partner in your immediate area."
    },
    {
        question: "How quickly can I launch my business?",
        answer: "We move fast. Once your agreement is finalized, the entire setup process—from cart fabrication and delivery to your training and grand opening—typically takes just 2 to 3 weeks."
    },
    {
        question: "Do you offer financing options?",
        answer: "We are actively collaborating with financial institutions to provide structured financing for our partners. In the meantime, the low initial investment makes it highly accessible for standard small business loans from local banks."
    },
    {
        question: "Can I expand and operate multiple carts?",
        answer: "Yes! In fact, we encourage it. Once you've successfully established your first location, our scalable model makes it incredibly easy to add secondary carts and multiply your revenue streams across new territories."
    },
    {
        question: "What ongoing support will I receive?",
        answer: "You're never in business alone. We provide dedicated WhatsApp and phone support during business hours for day-to-day operational queries. Additionally, we host quarterly partner meetups to share business insights, introduce new products, and help you continuously grow your footprint."
    },
    {
        question: "Is this a viable option for secondary or passive income?",
        answer: "Definitely. Many of our partners successfully run Rainbow Trucks as a secondary income stream. The streamlined operations and minimal daily management required make it an ideal, flexible business for those looking to diversify their income without leaving their primary careers."
    }
];

const AccordionItem = ({ faq, isOpen, onClick }) => {
    const contentRef = useRef(null);

    return (
        <div className="border-b border-slate-200">
            <button
                className="w-full py-6 sm:py-8 flex items-center justify-between text-left focus:outline-none group"
                onClick={onClick}
                aria-expanded={isOpen}
            >
                <h3 className={`text-[17px] sm:text-[20px] font-semibold transition-colors duration-200 pr-8 leading-snug ${isOpen ? 'text-[#73C8BD]' : 'text-slate-900 group-hover:text-[#73C8BD]'}`}>
                    {faq.question}
                </h3>
                <span className={`flex-shrink-0 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180 text-[#73C8BD]' : 'text-slate-400 group-hover:text-[#73C8BD]'}`}>
                    <LuChevronDown className="w-6 h-6 sm:w-7 sm:h-7" />
                </span>
            </button>
            
            <div 
                ref={contentRef}
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{ 
                    maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px',
                    opacity: isOpen ? 1 : 0
                }}
            >
                <div className="pb-8 pr-4 sm:pr-12">
                    <p className="text-slate-500 text-base sm:text-lg leading-relaxed font-normal">
                        {faq.answer}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default function Faq() {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section className="py-20 sm:py-32 bg-white relative">
            <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
                
                {/* Header Section */}
                <div className="text-center mb-16 sm:mb-20">
                    <SplitText
                        text="Frequently Asked Questions"
                        tag="h2"
                        className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 tracking-tight mb-6"
                        delay={30}
                        duration={0.6}
                        ease="power3.out"
                        splitType="words, chars"
                        from={{ opacity: 0, y: 30 }}
                        to={{ opacity: 1, y: 0 }}
                        threshold={0.1}
                        rootMargin="-40px"
                    />
                    <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto font-medium">
                        Everything you need to know about partnering with Rainbow Trucks.
                    </p>
                </div>

                {/* FAQ Accordion List */}
                <div className="w-full border-t border-slate-200">
                    {faqs.map((faq, index) => (
                        <AccordionItem 
                            key={index} 
                            faq={faq} 
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                        />
                    ))}
                </div>
                
            </div>
        </section>
    );
}
